import { ResourceConfigurationInterface } from '@cbsinteractive/avia-js';
import { ClickThroughNavigation } from '@cbsinteractive/avia-js-gam';
import { APIStreamType } from 'library/ts/enums/metadata/api/APIStreamType';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { MPXAssetType } from 'library/ts/model/enums/mpx/MPXAssetType';
import { PlayerAdapterVideoFormat } from 'library/ts/player/adapter/enums/PlayerAdapterVideoFormat';
import { IFreewheelRequest } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelRequest';
import { FreewheelDaiSdkRequest } from 'tag/ts/tag/ads/freewheel/requests/FreewheelDaiSdkRequest';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { PlayerLoadingTimeout } from 'tag/ts/tag/model/type/PlayerLoadingTimeout';
import { AbstractDesktopResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractDesktopResourceConfigBuilder';
import { PIDPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/utilities/PIDPlaybackUtility';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Desktop/Mobile Web resource configuration builder for PID-based content playback
 *
 * **PID Playback Configuration:**
 * Creates VTG ResourceConfiguration for Desktop and Mobile Web platforms using
 * Paramount's PID (Paramount ID) content identifier system. PIDs reference
 * content in MPX with complete metadata and stream configuration.
 *
 * **PID Content Resolution:**
 * - **Content Metadata**: Loads title, description, thumbnails from MPX via PID
 * - **Stream URLs**: Resolves adaptive streaming manifests (DASH/HLS) from PID
 * - **DRM Configuration**: Applies content protection based on MPX license rules
 * - **Ad Configuration**: Sets up monetization based on content and user context
 *
 * **Desktop Platform Optimizations:**
 * - **Full Feature Set**: Complete ad integration, quality controls, analytics
 * - **DRM Support**: Widevine, PlayReady for premium content protection
 * - **Quality Management**: Bitrate adaptation and manual quality selection
 * - **Enhanced Analytics**: Full tracking and measurement integration
 *
 * **Resource Configuration Pipeline:**
 * 1. **Base Desktop Config**: Inherits platform-specific settings
 * 2. **PID Resolution**: Uses PIDPlaybackUtility for content resolution
 * 3. **Media URL Setup**: Configures adaptive streaming endpoints
 * 4. **DRM Application**: Applies content protection rules
 * 5. **Ad Integration**: Configures Freewheel DAI, IMA, or GAM
 * 6. **Quality Settings**: Sets bitrate and adaptation parameters
 * 7. **Resource Sanitization**: Validates and cleans configuration
 */
export class DesktopPIDResourceConfigBuilder extends AbstractDesktopResourceConfigBuilder {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'DesktopPIDResourceConfigBuilder',
				...argument
			} );
		this.assetPlaybackUtil = new PIDPlaybackUtility( argument );
	}

	/**
	 * @description Builds complete VTG resource configuration for PID content on Desktop/Mobile Web
	 *
	 * **Configuration Build Process:**
	 * Creates a complete ResourceConfiguration by applying PID-specific settings
	 * on top of the base Desktop platform configuration. Handles content resolution,
	 * DRM setup, ad configuration, and quality management.
	 *
	 * **Build Steps:**
	 * 1. **Base Configuration**: Gets Desktop platform defaults from parent class
	 * 2. **Media URL Resolution**: Sets streaming URLs via PIDPlaybackUtility
	 * 3. **DRM Configuration**: Applies content protection based on MPX metadata
	 * 4. **Start Time Setup**: Configures playback position (live vs VOD)
	 * 5. **Thumbnail Configuration**: Sets preview image track URLs
	 * 6. **Ad Parameter Setup**: Configures monetization (Freewheel, GAM, IMA)
	 * 7. **Parameter Merging**: Applies Smart Tag parameter overrides
	 * 8. **International Handling**: Removes SSAI for international ad-free content
	 * 9. **Resource Sanitization**: Validates and cleans final configuration
	 * 10. **Test Overrides**: Applies development/testing parameter overrides
	 *
	 * **Error Handling:**
	 * - **Configuration Validation**: Ensures required fields are present
	 * - **Fallback Handling**: Provides default values for missing parameters
	 * - **Ad Integration Errors**: Continues playback even if ad setup fails
	 *
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>} Complete VTG resource configuration
	 */
	getResult(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> {
		return new Promise(
			( resolve, reject ) => {

				// get config prefilled with values for Desktop playback
				super.getResult().then(
					( data ) => {
						let resource: ResourceConfigurationInterface = data?.resource;

						// set values for PID playback
						resource = this.setMediaUrl( resource );
						resource = this.setDrm( resource ); // set drm
						resource = this.setThumbnailTrackUrl( resource );
						resource = this.setAdParameters( resource );
						resource = this.deepMergeResourceFromSmartTagParams( resource );
						resource = this.setStartTime( resource );
						resource = this.removeSsaiWhenAdsDisabledForIntl( resource );
						resource = this.sanitizeResource( resource );
						resource = this.testOverrides( resource );

						resolve( { msg: 'Resource config', resource: resource } );
					}
				).catch(
					( error ) => {
						// Desktop PID resource config failed, provide minimal fallback for video playback
						this?.logger?.warn( 'Desktop PID resource config failed, using minimal configuration:', error );

						// Create minimal resource as fallback to ensure PID videos can still play
						const basicResource = window.avia.createResourceConfig();
						resolve( { msg: 'Resource config (minimal)', resource: basicResource } );
					}
				);
			}
		);
	}

	/**
	 * @description Removes ssai from resource if video ads are disabled for INTL
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	removeSsaiWhenAdsDisabledForIntl( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if (
			this.tag.tagUtilities.isInternationalContext( this.tag ) &&
			this.params?.AD?.VIDEOADS?.enabled === false
		) {
			resource = this.removeSsai( resource );
			this?.logger?.log( 'removeSsaiWhenAdsDisabledForIntl', 'region is INTL, params.AD.VIDEOADS.enabled=false... removed ssai params from resource to fallback to playback via mediaUrl' );
		}
		return resource;
	}

	/**
	 * @description Sets Video thumbnail track if VTT sprite url is present in apiMetadata.thumbnailSet
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setThumbnailTrackUrl( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const url = this.apiMetadata.thumbnailSet?.find(
			( item ) => item.assetType === APIStreamType.VTT_SPRITE
		)?.url || undefined;

		if ( url ) {
			resource = super.setThumbnailTrackUrl( resource, url );
		} else {
			this?.logger?.warn( 'setThumbnailTrackUrl', 'could not find thumbnail url in apiMetadata.thumbnailSet... returning resource unmodified' );
		}

		return resource;
	}

	/**
	 * @description Sets media url in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setMediaUrl( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		return super.setMediaUrl( resource, this.assetPlaybackUtil?.getMediaUrl() );
	}

	/**
	 * @description Sets viewabilityTracking in resource config. Sets only partner in tracking object.
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setViewabilityTracking( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		// reset tracking object: https://jira.cbsi.com/browse/CBSVIDEO-3662
		const trackingObj = {
			partner: this.tag.model.contentUtilities.getPartnerID( this.params )
		} as any;
		super.setViewabilityTracking( resource, trackingObj );

		return resource;
	}

	/**
	 * @description Sets vastLoadTimeout as csai parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {number} timeout
	 * @return {ResourceConfigurationInterface}
	 */
	protected setVastLoadTimeout( resource: ResourceConfigurationInterface, timeout: number = undefined ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( timeout ) ||
			!this.utils.Type.isNumber( timeout )
		) {
			this?.logger?.warn( 'setVastLoadTimeout', 'missing/invalid arguments... returning resource unmodified', 'timeout:', timeout );
			return resource;
		}

		resource = this.setCsaiParameter( resource, 'vastLoadTimeout', timeout );

		return resource;
	}

	/**
	 * @description Sets loadVideoTimeoutMs as csai parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {number} timeout
	 * @return {ResourceConfigurationInterface}
	 */
	protected setLoadVideoTimeoutMs( resource: ResourceConfigurationInterface, timeout: number = undefined ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( timeout ) ||
			!this.utils.Type.isNumber( timeout )
		) {
			this?.logger?.warn( 'setLoadVideoTimeoutMs', 'missing/invalid arguments... returning resource unmodified', 'timeout:', timeout );
			return resource;
		}

		resource = this.setCsaiParameter( resource, 'loadVideoTimeoutMs', timeout );

		return resource;
	}

	/**
	 * @description Sets adCallUrl as csai parameter
	 * @param {ResourceConfigurationInterface} resource
	 * @param {string} url
	 * @return {ResourceConfigurationInterface}
	 */
	protected setAdCallUrl( resource: ResourceConfigurationInterface, url: string ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( resource ) ||
			this.utils.Type.isEmpty( url ) ||
			!this.utils.Type.isString( url )
		) {
			this?.logger?.warn( 'setAdCallUrl', 'missing/invalid arguments... returning resource unmodified', 'url:', url );
			return resource;
		}

		resource = this.setCsaiParameter( resource, 'adCallUrl', url );

		return resource;
	}

	/**
	 * @description Determines and sets any required values for ad playback
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setAdParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
		if ( adSetupInfo?.setup?.definition === AdSetupDefinition.NONE ) {
			this?.logger?.log( 'setAdParameters:', 'adSetupInfo.setup.definition=NONE... no ad setup required... returning resource unmodified' );
			return resource;
		}
		resource = this.setViewabilityTracking( resource );
		resource = this.setSsaiParameters( resource );
		this?.logger?.log( 'setAdParameters:', 'set resource ad parameters', resource );
		return resource;
	}

	/**
	 * Sets ssai parameters in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected setSsaiParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();

		if ( adSetupInfo?.setup?.definition === AdSetupDefinition.DAI_SDK ) {

			// remove ssai if VODFather is enabled but cannot set valid contentSourceId
			if (
				this.tag?.vodFatherUtilities?.isEnabled() &&
				this.utils.Type.isEmpty( this.tag?.vodFatherUtilities?.getDaiCmsId() )
			) {
				this.logger?.warn( 'setSsaiParameters:', 'VODFather is enabled but daiCmsId is unset, contentSourceId cannot be set... removing ssai from resource' );
				resource = this.removeSsai( resource );
				return resource;
			}

			// set parameters needed for DAI SDK ad playback as ssai parameters
			resource = this.setDaiSdkAdParameters( resource );

			// set millstone DAI parameters as ssai ad parameters
			resource = this.setMillstoneDAIParams( resource );
			return resource;
		}

		if ( adSetupInfo?.setup?.definition === AdSetupDefinition.LINEAR_AD_TRACKING ) {
			return this.setLinearAdParameters( resource );
		}

		this.logger?.log( 'setSsaiParameters:', 'ad setup unknown/unsupported, exiting', adSetupInfo );
		return resource;
	}

	/**
	 * @description Sets parameters in resource config which are required for ad playback via DAI SDK
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected setDaiSdkAdParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
		const daiSdkRequest = this.createFreewheelRequest( adSetupInfo ) as FreewheelDaiSdkRequest;
		// required by avia ad adapter plugin => set live flag
		resource = this.setIsLive( resource, this.tag.model.contentUtilities.isLive( this.params, this?.tag?.model?.apiMetadata ) );
		// required by avia ad adapter plugin => set asset key
		resource = this.setAssetKey( resource, this.tag.model.contentUtilities.getDAIAssetKey( this.params, this?.tag?.model?.apiMetadata ) );
		resource = this.setFreewheelParameters( resource, daiSdkRequest );
		resource = this.setDaiSdkVodParameters( resource );
		// enable nonce for INTL when using DAI
		resource = this.setEnableNonce( resource, this.tag.tagUtilities.isInternationalContext( this.tag ) );
		resource = this.setCustomUiOptions( resource );
		this?.logger?.log( 'setDaiSdkAdParameters:', 'set parameters required for DAI SDK ad setup', 'daiSdkRequest:', daiSdkRequest, 'resource:', resource );
		return resource;
	}

	/**
	 * @description Sets parameters in resource config which are required for ad playback via linear ads
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected setLinearAdParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		// CLID3-only (linear ad tracking): configure CLID3 as the primary ad adapter.
		if ( this.utils.Type.isEmpty( resource?.ad ) ) {
			resource.ad = {};
		}

		if ( this.utils.Type.isEmpty( resource?.ad?.adapter ) ) {
			resource.ad.adapter = {};
		}
		resource.ad.adapter.id = 'clid3';

		// remove CSAI and SSAI when CLID3 is the adapter.
		resource = this.removeCsai( resource );
		resource = this.removeSsai( resource );

		// VAST load time timeout override to solve VAST load timeouts
		resource = this.setVastLoadTimeout( resource, PlayerLoadingTimeout.VAST );
		// override ad load timeout
		resource = this.setLoadVideoTimeoutMs( resource, PlayerLoadingTimeout.AD ); // Ad Adapter plugin
		this?.logger?.log( 'setLinearAdParameters', 'set parameters required for linear ad setup', 'resource:', resource );
		return resource;
	}

	/**
	 * @description Sets Freewheel parameters in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @param {IFreewheelRequest} request
	 * @return {ResourceConfigurationInterface}
	 */
	protected setFreewheelParameters( resource: ResourceConfigurationInterface, request: IFreewheelRequest ): ResourceConfigurationInterface {
		// validate
		if ( this.utils.Type.isEmpty( request.parameters ) ) {
			this?.logger?.warn( 'setFreewheelParameters', 'Freewheel request has no parameters... exiting', 'request:', request, 'resource:', resource );
			return resource;
		}

		// init
		if ( this.utils.Type.isEmpty( resource?.ad?.ssai ) ) {
			resource = this.initSsai( resource );
		}
		resource = this.initSsaiAdParameter( resource );

		// add all params to adParameters, add bundle to adParameters.cust_params
		// INFO: DAI SDK does encoding for us so we should not do any url encoding ourselves
		request.parameters
			.filter( ( param ) => {
				if ( this.utils.Type.isEmpty( param.value ) ) {
					this?.logger?.log( 'setFreewheelParameters', 'filtering out param with unset value...', 'param:', param );
					return false;
				}
				return true;
			} )
			.forEach( ( param ) => {
				if (
					param.key === 'bundle' ||
					param.key === 'imafw_bundle'
				) {
					// avia requires cust_param must be a string, as per VTG
					// https://support.google.com/admanager/answer/7320899?hl=en#cust_params
					// HTML5, and Chromecast:
					//	Encode the values only
					resource = this.setCustParams( resource, `bundle=${param.value}` );
				} else {
					resource = this.setSsaiAdParameter( resource, param.key, param.value );
				}
			} );
		this?.logger?.log( 'setFreewheelParameters', 'set Freewheel parameters', 'resource:', resource );
		return resource;
	}

	/**
	 * @description Sets parameters required when playing VOD via DAI SDK
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected setDaiSdkVodParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		// validate
		if ( this.TagConstants.DAI_SDK_VOD_ENABLED === false ) {
			this.logger?.warn( 'setDaiSdkVodParameters:', 'DAI_SDK_VOD_ENABLED flag is disabled... exiting', resource );
			return resource;
		}
		if ( this.tag.model.contentUtilities.isLive( this.params ) ) {
			this.logger?.warn( 'setDaiSdkVodParameters:', 'content is Live... exiting', resource );
			return resource;
		}

		resource = this.setContentSourceId( resource );

		// set videoId to contentId (not pid) from apiMetadata
		resource = this.setVideoId( resource, this?.tag?.model?.apiMetadata?.contentId );
		// set ssai format
		this.setFormat( resource );
		this.logger?.log( 'setDaiSdkVodParameters:', 'set parameters required for VOD playback via DAI SDK', resource );
		return resource;
	}

	/**
	 * @description Sets contentSourceId as SSAI parameter. Determines value depending on whether VODFather is enabled.
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setContentSourceId( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( this.tag?.vodFatherUtilities?.isEnabled() ) {
			const daiCmsId = this.tag?.vodFatherUtilities?.getDaiCmsId();
			this.logger?.log( 'setContentSourceId', 'VODFather is enabled, setting contentSourceId to daiCmsId:', daiCmsId );
			return super.setContentSourceId( resource, daiCmsId );
		}
		this.logger?.log( 'setContentSourceId', 'VODFather is disabled, setting contentSourceId to AdOps constant:', this.TagConstants?.ADOPS_ID?.CMSID );
		return super.setContentSourceId( resource, this.TagConstants?.ADOPS_ID?.CMSID );
	}

	/**
	 * @description Set custom UI options in SSAI for DAI SDK content
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setCustomUiOptions( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( !resource.ad?.ssai ) {
			this.logger?.warn( 'setCustomUiOptions:', 'SSAI configuration is missing in resource... exiting' );
			return resource;
		}

		super.setCustomUiOptions( resource, {
			enabled: true,
			skippableSupport: true,
			clickThroughNavigation: ClickThroughNavigation.EXTERNAL
		} );

		return resource;
	}

	/**
	 * Sets format as SSAI parameter based on current asset's assetType
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected setFormat( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		switch ( this?.tag?.model?.apiMetadata?.assetType ) {
			case MPXAssetType.DASH_CBCS_CMAF:
			case MPXAssetType.DASH_CENC:
			case MPXAssetType.DASH_CENC_HDR:
			case MPXAssetType.DASH_CENC_HDR10:
			case MPXAssetType.DASH_CENC_LGCY:
			case MPXAssetType.DASH_CENC_PRECON:
			case MPXAssetType.DASH_CMAF:
			case MPXAssetType.DASH_LIVE:
			case MPXAssetType.DASH_TA:
				resource = super.setFormat( resource, PlayerAdapterVideoFormat.DASH );
				break;
			default:
				this?.logger?.warn( 'setFormat:', 'invalid/unsupported apiMetadata assetType, could not set resource.ad.ssai.format', 'assetType:', this?.tag?.model?.apiMetadata?.assetType );
				break;
		}
		return resource;
	}
}